import React from 'react'

import { QuestionResponse } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'

import styles from './outcomesResponseDetails.module.scss'
import { outcomeChartConstants, RCADS_PARENT_ANSWER_INSTRUCTIONS } from '../../constants/outcomeConstants'
import outcomeUtils from '../../utils/outcomeUtils'

const NUMERIC_ANSWER_GROUPS = ['TFM', 'PARENTING-TASK', 'ROI-TOTAL', 'ROI-P-TOTAL']

export type OutcomeResponseDetailsViewData = {
  clientName: string
  title: string | undefined
  dateSubmitted: string
  dateStarted: string
  assessmentType: string
  /** Displayed as-is and used for the severity lookup, so it must stay numeric. */
  score: number | string | null | undefined
  /** Omit the aggregate score for question sets, such as Alliance, with only individual ratings. */
  hideFinalScore?: boolean
  /**
   * Optional copy shown above the answers. Omit to preserve the legacy
   * assessment-specific copy, or pass `null` to intentionally omit it.
   */
  answerDescription?: React.ReactNode | null
  answers: QuestionResponse[] | undefined
}

const OutcomeResponseDetails: React.FC<OutcomeResponseDetailsProps> = ({ viewData }) => {
  const assessment = outcomeChartConstants.find((obj) => obj.name === viewData?.assessmentType)
  const ranges = assessment?.ranges
  const createAndSubmitDatesAreTheSame = viewData?.dateStarted === viewData?.dateSubmitted

  const getAnswerDescription = () => {
    switch (viewData?.assessmentType) {
      case 'PSS':
      case 'WELL-BEING':
        return (
          <p>
            Over the <b>last week</b>, how often have you experienced the following?
          </p>
        )
      case 'TFM':
      case 'TLFB-ALCOHOL':
        return <p>This past week, how many drinks did you consume on each day?</p>
      case 'WSAS':
        return (
          <p>
            How we feel due to our mental health challenges (e.g., anxiety, depression, stress, sleep) can impact our
            day-to-day functioning. Please read each statement and rate how much your mental health challenges have
            impaired your ability to do things <b>in the past week</b>.
          </p>
        )
      case 'PCL5':
        return (
          <p>
            Below is a list of problems that people sometimes have in response to a very stressful experience. Please
            read each problem carefully and then select the appropriate answer to indicate how much you have been
            bothered by that problem <b>in the past month</b>.
          </p>
        )
      case 'PARENTAL-STRESS':
        return (
          <p>
            The following statements describe feelings and perceptions about the experience of being a parent. Think of
            each of the items in terms of how your relationship with your child or children typically is. Please
            indicate the degree to which you agree or disagree with the following items by selecting the appropriate
            response.
          </p>
        )
      case 'TLFB-OTHER-SUBSTANCES':
      case 'BDD':
      case 'PARENTING-TASK':
      case 'CGSQ':
      case 'LEAPS':
      case 'ROI-TOTAL':
      case 'ROI-P-TOTAL':
        return <></>
      case 'RCADS-ANXIETY':
      case 'RCADS-DEPRESSION':
      case 'RCADS-TOTAL':
        return (
          <p>
            Please select the option that shows how often each of these things happen to you. There are no right or
            wrong answers.
          </p>
        )
      case 'RCADS-P-TOTAL':
        return <p>{RCADS_PARENT_ANSWER_INSTRUCTIONS}</p>
      default:
        if (assessment?.answerInstructions) {
          return <p>{assessment.answerInstructions}</p>
        }
        return (
          <p>
            Over the <b>last week</b>, how often have you been bothered by any of the following problems?
          </p>
        )
    }
  }

  const answerDescription =
    viewData?.answerDescription === undefined ? getAnswerDescription() : viewData.answerDescription

  return (
    <div>
      <h2 className={styles['main-header']} data-test-id='OutcomeResponseDetails-mainHeader'>
        {viewData?.title || viewData?.assessmentType}
      </h2>
      <div className={styles['overview-container']}>
        <div>
          <h4 data-test-id='OutcomeResponseDetails-dateStarted-header'>
            {createAndSubmitDatesAreTheSame ? 'Started and submitted' : 'Assessment started'}
          </h4>
          <p data-test-id='OutcomeResponseDetails-dateStarted-value'>
            {createAndSubmitDatesAreTheSame ? viewData?.dateSubmitted : viewData?.dateStarted}
          </p>
        </div>
        {!createAndSubmitDatesAreTheSame && (
          <div>
            <h4 data-test-id='OutcomeResponseDetails-dateSubmitted-header'>Submitted</h4>
            <p data-test-id='OutcomeResponseDetails-dateSubmitted-value'>{viewData?.dateSubmitted}</p>
          </div>
        )}
        {!viewData?.hideFinalScore && (
          <div>
            <h4 data-test-id='OutcomeResponseDetails-finalScore-header'>Final Score</h4>
            <p data-test-id='OutcomeResponseDetails-finalScore-value'>
              {viewData?.score}{' '}
              {outcomeUtils.convertOutcomeScoreToDescription(viewData?.assessmentType, viewData?.score)}
            </p>
          </div>
        )}
      </div>
      {viewData?.answerDescription !== null && (
        <div data-test-id='OutcomeResponseDetails-description'>{answerDescription}</div>
      )}
      <div className={styles['list-container']}>
        {viewData?.answers?.map((value, index) => {
          return (
            <div className={styles['list-item']} key={value['question-number']}>
              <p className={styles.counter}>{index + 1} -</p>
              <p className={styles.question}>{value.question}</p>
              <p className={styles.answer}>
                {value['answer-text'] ||
                  (value.groups.some((group) => NUMERIC_ANSWER_GROUPS.some((name) => group.includes(name))) &&
                    value.score)}
              </p>
            </div>
          )
        })}
      </div>
      {(assessment?.individualAnswerDescriptions || assessment?.finalScoreDescriptions) && (
        <div className={styles['score-key']}>
          {assessment?.individualAnswerDescriptions && (
            <div>
              <h4>Individual Answers Range</h4>
              {assessment?.answers?.map((answer) => (
                <p key={answer?.value}>
                  <span>{answer?.value}</span>
                  {answer?.label}
                </p>
              ))}
            </div>
          )}
          {assessment?.finalScoreDescriptions && (
            <div className={styles['range-container']}>
              <h4>Final Score Range</h4>
              {ranges?.map((range) => (
                <p key={range?.lower}>
                  <span>
                    {range?.lower}
                    {range?.upper ? '-' + range?.upper : '+'}
                  </span>
                  {range?.description}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type OutcomeResponseDetailsProps = { viewData: OutcomeResponseDetailsViewData }

export default OutcomeResponseDetails
